import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { createSemanticTileController, type SemanticTileController } from '../services/googleMaps';

interface Props {
  geometry?: any;
  rawEvents?: any[];
  acceptedVertices?: any[];
  height?: string;
}

const MappingEvidenceReviewMap: React.FC<Props> = ({ geometry, rawEvents = [], acceptedVertices = [], height = '360px' }) => {
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileRef = useRef<SemanticTileController | null>(null);
  useEffect(() => {
    if (!elRef.current) return;
    const first = rawEvents[0];
    const center: [number, number] = first ? [Number(first.latitude), Number(first.longitude)] : [4.6333, 9.45];
    const map = L.map(elRef.current, { center, zoom: 16, attributionControl: true });
    mapRef.current = map;
    tileRef.current = createSemanticTileController(map, 'satellite');
    L.control.scale({ metric: true, imperial: false }).addTo(map);
    const bounds: L.LatLngExpression[] = [];
    if (rawEvents.length > 1) {
      const raw = rawEvents.map(e => [Number(e.latitude), Number(e.longitude)] as [number, number]);
      L.polyline(raw, { weight: 2, opacity: 0.55, dashArray: '4 5' }).addTo(map);
      bounds.push(...raw);
      rawEvents.filter(e => !e.accepted_for_boundary).forEach(e => L.circleMarker([Number(e.latitude), Number(e.longitude)], { radius: 3, weight: 1, fillOpacity: 0.55 }).bindTooltip(`Rejected · ${e.rejection_reason || 'policy'}`).addTo(map));
    }
    if (acceptedVertices.length) {
      const accepted = acceptedVertices.map(v => [Number(v.latitude), Number(v.longitude)] as [number, number]);
      accepted.forEach((pt, i) => L.circleMarker(pt, { radius: 4, weight: 2, fillOpacity: 0.85 }).bindTooltip(`Accepted vertex ${i + 1}`).addTo(map));
      bounds.push(...accepted);
    }
    if (geometry?.type === 'Polygon' && geometry.coordinates?.[0]) {
      const ring = geometry.coordinates[0].map((c: number[]) => [c[1], c[0]] as [number, number]);
      L.polygon(ring, { weight: 3, fillOpacity: 0.12 }).bindTooltip('Candidate geometry').addTo(map);
      bounds.push(...ring);
    }
    if (bounds.length) map.fitBounds(L.latLngBounds(bounds), { padding: [24, 24], maxZoom: 18 });
    const ro = new ResizeObserver(() => map.invalidateSize({ pan: false }));
    ro.observe(elRef.current);
    return () => { ro.disconnect(); tileRef.current?.destroy(); map.remove(); mapRef.current = null; };
  }, [geometry, rawEvents, acceptedVertices]);
  return <div ref={elRef} style={{ height, width: '100%' }} className="rounded-xl overflow-hidden border border-ink-200 dark:border-ink-800" aria-label="Mapping evidence review map" />;
};
export default MappingEvidenceReviewMap;
